import React, { useState, useMemo, useEffect } from "react";
import {
  Stack,
  Tabs,
  Paper,
  Button,
  ScrollArea,
  Loader,
  Center,
} from "@mantine/core";
import WebsiteGeneratorOptionCard, {
  ThemeIcon,
  FontsIcon,
  ColorsIcon,
} from "./WebsiteGeneratorOptionCard";
import ThemeSection from "./ThemeSection";
import FontsSection from "./FontsSection";
import ColorsSection from "./ColorsSection";
import WebsiteGeneratorContentOptions from "./WebsiteGeneratorContentOptions";
import WebsiteGeneratorRequiredSectionsContent from "./WebsiteGeneratorRequiredSectionsContent";
import WebsiteGeneratorOptionalSectionsContent from "./WebsiteGeneratorOptionalSectionsContent";
import { FieldSchema, ValidationDict } from "../../../types/staticWebGenerator";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_WHITE = "#ffffff";

const STYLE_OPTIONS = [
  {
    id: "theme",
    icon: <ThemeIcon />,
    title: "Theme",
    description: "Choose your template",
  },
  {
    id: "fonts",
    icon: <FontsIcon />,
    title: "Fonts",
    description: "Customize typography",
  },
  {
    id: "colors",
    icon: <ColorsIcon />,
    title: "Colors",
    description: "Adjust color scheme",
  },
];

interface HeroContent {
  title?: string;
  description?: string;
  body?: string;
  photos?: Array<{ url?: string; src?: string; filename?: string }>;
  includeBookingButton?: boolean;
  heroBookingCopy?: string;
}

interface ServicesContent {
  title?: string;
}

interface TeamContent {
  title?: string;
  body?: string;
}

interface ContactContent {
  showDirections?: boolean;
  directions?: string;
  enableForm?: boolean;
  formEmail?: string;
  showMap?: boolean;
}

interface FooterContent {
  showSocialIcons?: boolean;
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
}

interface BookingLinkContent {
  copy?: string;
  useCustomLink?: boolean;
  link?: string;
}

interface WebsiteGeneratorDrawerProps {
  selectedTheme: string;
  onThemeSelect: (theme: string) => void;
  headingFont: string;
  bodyFont: string;
  backgroundColor: string;
  textColor: string;
  onHeadingFontChange: (font: string | null) => void;
  onBodyFontChange: (font: string | null) => void;
  onBackgroundColorChange: (color: string) => void;
  onTextColorChange: (color: string) => void;
  onResetToDefault: () => void;
  // Content props
  heroContent?: HeroContent;
  onHeroContentChange?: (content: HeroContent) => void;
  heroImageStateArray?: any[];
  onHeroImageStateChange?: (images: any[]) => void;
  selectedHeroPhoto?: any;
  onHeroPhotoSelect?: (photo: any) => void;
  servicesContent?: ServicesContent;
  onServicesContentChange?: (content: ServicesContent) => void;
  teamContent?: TeamContent;
  onTeamContentChange?: (content: TeamContent) => void;
  contactContent?: ContactContent;
  onContactContentChange?: (content: ContactContent) => void;
  footerContent?: FooterContent;
  onFooterContentChange?: (content: FooterContent) => void;
  bookingLinkContent?: BookingLinkContent;
  onBookingLinkContentChange?: (content: BookingLinkContent) => void;
  // Optional sections (accepted but not used here; required by Wizard typing)
  galleryContent?:
    | any[]
    | { title?: string; items?: any[]; gridLayout?: string };
  onGalleryContentChange?: (
    items: any[] | { title?: string; items?: any[]; gridLayout?: string },
  ) => void;
  testimonialsContent?: any;
  onTestimonialsContentChange?: (content: any) => void;
  announcementBannerContent?: any;
  onAnnouncementBannerContentChange?: (content: any) => void;
  bannerContent?: any;
  onBannerContentChange?: (content: any) => void;
  highlightsContent?: any;
  onHighlightsContentChange?: (content: any) => void;
  optionalSectionStates?: any;
  onOptionalSectionStatesChange?: (s: any) => void;
  // Schema and validation props
  fieldSchema?: FieldSchema | null;
  missingFields?: string[];
  validationDict?: ValidationDict;
  // Loading state props
  isLoadingPreviewData?: boolean;
  isTemplateLoading?: boolean;
}

const WebsiteGeneratorDrawer: React.FC<WebsiteGeneratorDrawerProps> = ({
  selectedTheme,
  onThemeSelect,
  headingFont,
  bodyFont,
  backgroundColor,
  textColor,
  onHeadingFontChange,
  onBodyFontChange,
  onBackgroundColorChange,
  onTextColorChange,
  onResetToDefault,
  // Content props
  heroContent,
  onHeroContentChange,
  heroImageStateArray,
  onHeroImageStateChange,
  selectedHeroPhoto,
  onHeroPhotoSelect,
  servicesContent,
  onServicesContentChange,
  teamContent,
  onTeamContentChange,
  contactContent,
  onContactContentChange,
  footerContent,
  onFooterContentChange,
  bookingLinkContent,
  onBookingLinkContentChange,
  // Optional sections
  galleryContent,
  onGalleryContentChange,
  testimonialsContent,
  onTestimonialsContentChange,
  announcementBannerContent,
  onAnnouncementBannerContentChange,
  bannerContent,
  onBannerContentChange,
  highlightsContent,
  onHighlightsContentChange,
  optionalSectionStates,
  onOptionalSectionStatesChange,
  fieldSchema,
  missingFields = [],
  validationDict = {},
  isLoadingPreviewData = false,
  isTemplateLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<string | null>("style");
  const [styleSection, setStyleSection] = useState("options"); // "options" or specific content
  const [contentSection, setContentSection] = useState("options"); // "options" or specific content

  // Show loading spinner if preview data is loading or template is loading
  const showLoading = isLoadingPreviewData || isTemplateLoading;

  // Check if style tab has missing required fields
  const hasMissingStyleFields = useMemo(() => {
    // Check if colors section has any errors
    const colorsHasError =
      validationDict.colors &&
      Object.keys(validationDict.colors).length > 0 &&
      Object.values(validationDict.colors).some((v) => v === true);

    // Check if fonts section has any errors
    const fontsHasError =
      validationDict.fonts &&
      Object.keys(validationDict.fonts).length > 0 &&
      Object.values(validationDict.fonts).some((v) => v === true);

    const hasError = colorsHasError || fontsHasError;
    return hasError;
  }, [validationDict]);

  // Check if content tab has missing required fields
  const hasMissingContentFields = useMemo(() => {
    // Note: "team" is optional, handled by hasOptionalSectionsError
    const contentSections = [
      "hero",
      "services",
      "contact",
      "footer",
      "bookingLink",
    ];
    const sectionErrors = contentSections.map((section) => {
      const sectionValidation = validationDict[section];
      const hasError =
        sectionValidation &&
        Object.values(sectionValidation).some((v) => v === true);
      return { section, hasError, validation: sectionValidation };
    });
    const hasError = sectionErrors.some((se) => se.hasError);
    return hasError;
  }, [validationDict]);

  // Check if required sections have missing fields
  const hasRequiredSectionsError = useMemo(() => {
    // Note: "team" is an optional section, not required
    const requiredSections = [
      "hero",
      "services",
      "contact",
      "footer",
      "bookingLink",
    ];
    const sectionErrors = requiredSections.map((section) => {
      const sectionValidation = validationDict[section];
      const hasError =
        sectionValidation &&
        Object.values(sectionValidation).some((v) => v === true);
      return { section, hasError, validation: sectionValidation };
    });
    const hasError = sectionErrors.some((se) => se.hasError);
    return hasError;
  }, [validationDict]);

  const renderStyleOptions = () => (
    <Stack gap="sm">
      {STYLE_OPTIONS.map((option) => (
        <WebsiteGeneratorOptionCard
          key={option.id}
          icon={option.icon}
          title={option.title}
          description={option.description}
          onClick={() => setStyleSection(option.id)}
        />
      ))}
    </Stack>
  );

  const renderStyleContent = () => {
    switch (styleSection) {
      case "options":
        return renderStyleOptions();

      case "theme":
        return (
          <ThemeSection
            selectedTheme={selectedTheme}
            onThemeSelect={onThemeSelect}
            onBack={() => setStyleSection("options")}
          />
        );

      case "fonts":
        return (
          <FontsSection
            headingFont={headingFont}
            bodyFont={bodyFont}
            onHeadingFontChange={onHeadingFontChange}
            onBodyFontChange={onBodyFontChange}
            onBack={() => setStyleSection("options")}
          />
        );

      case "colors":
        return (
          <ColorsSection
            backgroundColor={backgroundColor}
            textColor={textColor}
            onBackgroundColorChange={onBackgroundColorChange}
            onTextColorChange={onTextColorChange}
            onBack={() => setStyleSection("options")}
          />
        );

      default:
        return renderStyleOptions();
    }
  };

  // Check if optional sections have missing fields (only if enabled)
  const hasOptionalSectionsError = useMemo(() => {
    // Check validationDict for ALL optional sections: gallery, testimonials, highlights, banner, announcementBanner, team
    const optionalSections = [
      "gallery",
      "testimonials",
      "highlights",
      "banner",
      "announcementBanner",
      "team",
    ];
    const hasError = optionalSections.some((section) => {
      const sectionValidation = validationDict[section];
      if (sectionValidation && Object.keys(sectionValidation).length > 0) {
        // Check if any field in this section has an error
        return Object.values(sectionValidation).some(
          (hasError) => hasError === true,
        );
      }
      return false;
    });
    return hasError;
  }, [validationDict]);

  const renderContentOptions = () => (
    <WebsiteGeneratorContentOptions
      onRequiredSectionsClick={() => setContentSection("required")}
      onOptionalSectionsClick={() => setContentSection("optional")}
      hasRequiredSectionsError={hasRequiredSectionsError}
      hasOptionalSectionsError={hasOptionalSectionsError}
    />
  );

  const renderContentContent = () => {
    switch (contentSection) {
      case "options":
        return renderContentOptions();

      case "required":
        return (
          <WebsiteGeneratorRequiredSectionsContent
            onBack={() => setContentSection("options")}
            heroContent={heroContent}
            onHeroContentChange={onHeroContentChange}
            heroImageStateArray={heroImageStateArray}
            onHeroImageStateChange={onHeroImageStateChange}
            selectedHeroPhoto={selectedHeroPhoto}
            onHeroPhotoSelect={onHeroPhotoSelect}
            servicesContent={servicesContent}
            onServicesContentChange={onServicesContentChange}
            contactContent={contactContent}
            onContactContentChange={onContactContentChange}
            footerContent={footerContent}
            onFooterContentChange={onFooterContentChange}
            bookingLinkContent={bookingLinkContent}
            onBookingLinkContentChange={onBookingLinkContentChange}
            missingFields={missingFields}
            fieldSchema={fieldSchema}
            validationDict={validationDict}
          />
        );

      case "optional":
        return (
          <WebsiteGeneratorOptionalSectionsContent
            onBack={() => setContentSection("options")}
            galleryContent={galleryContent as any[]}
            onGalleryContentChange={onGalleryContentChange as any}
            testimonialsContent={testimonialsContent}
            onTestimonialsContentChange={onTestimonialsContentChange as any}
            announcementBannerContent={announcementBannerContent}
            onAnnouncementBannerContentChange={
              onAnnouncementBannerContentChange
            }
            bannerContent={bannerContent}
            onBannerContentChange={onBannerContentChange}
            highlightsContent={highlightsContent}
            onHighlightsContentChange={onHighlightsContentChange}
            teamContent={teamContent}
            onTeamContentChange={onTeamContentChange}
            validationDict={validationDict}
            optionalSectionStates={optionalSectionStates as any}
            onOptionalSectionStatesChange={onOptionalSectionStatesChange as any}
            missingFields={missingFields}
            fieldSchema={fieldSchema}
          />
        );

      default:
        return renderContentOptions();
    }
  };

  return (
    <Paper
      p="md"
      style={{
        flex: 1, // Takes up 1/3 of the flex container (2:1 ratio with presentator)
        minWidth: "280px", // Reduced minimum width
        height: "100%",
        borderLeft: `1px solid ${COLOR_BORDER_LIGHT}`,
        backgroundColor: COLOR_BACKGROUND_WHITE,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack
        gap="md"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
          }}
        >
          <Tabs.List style={{ flexShrink: 0 }}>
            <Tabs.Tab
              value="style"
              style={{
                borderBottom: hasMissingStyleFields
                  ? "2px solid red"
                  : undefined,
              }}
            >
              Style {hasMissingStyleFields && " *"}
            </Tabs.Tab>
            <Tabs.Tab
              value="content"
              style={{
                borderBottom:
                  hasMissingContentFields || hasOptionalSectionsError
                    ? "2px solid red"
                    : undefined,
              }}
            >
              Content{" "}
              {(hasMissingContentFields || hasOptionalSectionsError) && " *"}
            </Tabs.Tab>
          </Tabs.List>

          {/* Content */}
          <ScrollArea
            style={{
              flex: 1,
              position: "relative",
              minHeight: 0,
            }}
            type="scroll"
          >
            <div style={{ padding: "8px 0" }}>
              {showLoading ? (
                <Center style={{ height: "100%", minHeight: "400px" }}>
                  <Loader size="lg" />
                </Center>
              ) : (
                <>
                  {activeTab === "style" && renderStyleContent()}
                  {activeTab === "content" && renderContentContent()}
                </>
              )}
            </div>
          </ScrollArea>
        </Tabs>

        {/* Reset Button */}
        <Button
          variant="subtle"
          color="blue"
          onClick={onResetToDefault}
          style={{ flexShrink: 0 }}
        >
          Reset to Theme Default
        </Button>
      </Stack>
    </Paper>
  );
};

export default WebsiteGeneratorDrawer;
